import { piecesGrid, updatePieceData } from './interfaces'

const apiURL = process.env.REACT_APP_API_URL

export const getPiecesGridData = async (): Promise<piecesGrid[]> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/Pieces`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to fetch pieces data')
  }
  
  const data = await response.json()
  return data
}

export const getPieceById = async (id: number) => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/Pieces/${id}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to fetch piece')
  }
  
  return await response.json()
}

export const getPiecesByJob = async (jobId: number): Promise<piecesGrid[]> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/Pieces/job/${jobId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to fetch pieces by job')
  }
  
  return await response.json()
}

export const getPiecesByDepartment = async (departmentId: number): Promise<piecesGrid[]> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/Pieces/department/${departmentId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to fetch pieces by department')
  }
  
  return await response.json()
}

export const getFlaggedPieces = async (): Promise<piecesGrid[]> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/Pieces/flagged`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to fetch flagged pieces')
  }
  
  return await response.json()
}

export const createPiece = async (pieceData: Partial<piecesGrid>) => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/Pieces`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(pieceData),
  })
  
  if (!response.ok) {
    throw new Error('Failed to create piece')
  }
  
  return await response.json()
}

export const updatePiece = async (id: number, pieceData: Partial<updatePieceData>) => {
  const token = localStorage.getItem('authToken')
  
  // Convert camelCase form data to snake_case API data
  const apiData: Partial<piecesGrid> = {
    piecesId: pieceData.piecesId,
    jobId: pieceData.jobId,
    supplierName: pieceData.supplierName,
    total: pieceData.total,
    subTotal: pieceData.subTotal,
    totalTax: pieceData.totalTax,
    invoiceId: pieceData.invoiceId,
    invoiceDate: pieceData.invoiceDate,
    dueDate: pieceData.dueDate,
    documentTypeId: pieceData.documentTypeId,
    description: pieceData.description,
    comments: pieceData.comments,
    flagged: pieceData.flagged,
    submitToXero: pieceData.submitToXero,
    xeroTrackingDepartmentId: pieceData.xeroTrackingDepartmentId,
    xeroAccountId: pieceData.xeroAccountId,
    xeroTaxRateId: pieceData.xeroTaxRateId,
    xeroTrackingTypeId: pieceData.xeroTrackingTypeId,
    xeroSupplierId: pieceData.xeroSupplierId,
  }
  

  console.log('Updating piece with ID:', id)
  console.log('Form data received:', pieceData)
  console.log('API data being sent:', apiData)
  
  const response = await fetch(`https://${apiURL}/Pieces/${id}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(apiData),
  })
  
  if (!response.ok) {
    const errorText = await response.text()
    console.error('Update piece failed:', {
      status: response.status,
      statusText: response.statusText,
      errorText
    })
    throw new Error(`Failed to update piece: ${response.status} ${response.statusText} - ${errorText}`)
  }
  
  return await response.json()
}

export const togglePieceFlag = async (id: number) => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/Pieces/${id}/toggle-flag`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to toggle piece flag')
  }
  
  return await response.json()
}

export const deletePiece = async (id: number) => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/Pieces/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to delete piece')
  }
  
  return await response.json()
}

export const getTotalByJob = async (jobId: number) => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/Pieces/job/${jobId}/total`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to get total by job')
  }
  
  return await response.json()
}

export const getTotalByDepartment = async (departmentId: number) => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/Pieces/department/${departmentId}/total`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to get total by department')
  }
  
  return await response.json()
}

export const getPieceFileUrl = async (id: number): Promise<string> => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/Pieces/${id}/file-url`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to fetch piece file URL')
  }
  
  const data = await response.json()
  // Assuming the API returns an object with a URL property, adjust if different
  return data.url || data.fileUrl || data
}

export const createXeroPurchaseInvoice = async (id: number) => {
  const token = localStorage.getItem('authToken')
  const response = await fetch(`https://${apiURL}/Pieces/${id}/create-xero-purchase-invoice`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
  
  if (!response.ok) {
    throw new Error('Failed to create Xero purchase invoice')
  }
  
  return await response.json()
}